import { Link, useRouter } from '@tanstack/react-router';
import { RotateCw, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';

import { Button, buttonVariants, Container } from '@/components/ui';

/**
 * Останній рубіж адмінки.
 *
 * Без нього будь-яка помилка рендера давала білий екран: React знімав дерево,
 * а маршрутизатор писав у консоль «this error wasn't caught by any route».
 * Оператор бачив «не запускається» і не мав що з цим робити. Тепер він бачить
 * причину і кнопку, яка перезапитує дані, не втрачаючи адреси.
 */
export function ErrorScreen({ error }: { error: Error }): ReactNode {
	const router = useRouter();

	return (
		<Container className="py-16">
			<div className="mx-auto max-w-lg rounded-card border border-border bg-surface p-6 text-center">
				<TriangleAlert className="mx-auto size-7 text-warning-strong" />
				<h1 className="mt-3 text-lg font-semibold">This page could not be shown</h1>
				<p className="mt-1 text-sm leading-relaxed text-fg-muted">
					Nothing was changed. This usually means the backend restarted or an answer came back in an unexpected
					shape — reloading the data is safe.
				</p>
				<p className="mt-3 break-words rounded-md bg-surface-muted px-3 py-2 text-left text-xs text-fg-muted">
					{error.message || String(error)}
				</p>
				<div className="mt-4 flex justify-center gap-2">
					<Button onClick={() => void router.invalidate()}>
						<RotateCw className="size-4" />
						Reload the data
					</Button>
					<Button variant="secondary" onClick={() => window.location.reload()}>
						Reload the page
					</Button>
				</div>
			</div>
		</Container>
	);
}

/** Адреса, якої немає: теж окремий екран, а не порожнє полотно. */
export function NotFoundScreen(): ReactNode {
	return (
		<Container className="py-16">
			<div className="mx-auto max-w-lg rounded-card border border-border bg-surface p-6 text-center">
				<h1 className="text-lg font-semibold">No such page</h1>
				<p className="mt-1 text-sm text-fg-muted">Check the address, or go back to the queue.</p>
				<Link to="/" className={buttonVariants({ variant: 'secondary', className: 'mt-4' })}>
					Back to the queue
				</Link>
			</div>
		</Container>
	);
}
